/**
 * Station Configuration Utilities
 * Handles mapping between station configuration values and actual station numbers
 */

/**
 * Get the stations controlled by a specific station configuration
 * @param {number} stationConfig - Station configuration (1-9)
 * @returns {number[]} Array of station numbers controlled by this config
 */
export function getStationsForConfig(stationConfig) {
  const stationMap = {
    1: [1, 2, 3, 4],     // Stations 1-4
    2: [5, 6, 7, 8],     // Stations 5-8 (config 2 for stations 5-8)
    3: [9, 10],          // Stations 9-10 (config 3 for stations 9-10)
    4: [13, 14, 15, 16], // Stations 13-16 (not used since max is 10)
    5: [5, 6, 7, 8],     // Stations 5-8 (config 5 ALSO maps to stations 5-8)
    6: [21, 22, 23, 24], // Stations 21-24 (not used since max is 10)
    7: [25, 26, 27, 28], // Stations 25-28 (not used since max is 10)
    8: [29, 30, 31, 32], // Stations 29-32 (not used since max is 10)
    9: [9, 10]           // Stations 9-10 (config 9 maps to stations 9-10)
  };
  
  return stationMap[stationConfig] || [];
}

/**
 * Get the station range name for display (e.g., "1-4", "5-8", "9-10")
 * @param {number} stationConfig - Station configuration
 * @returns {string} Range string for display
 */
export function getStationRangeName(stationConfig) {
  const stations = getStationsForConfig(stationConfig);
  if (stations.length === 0) return '';
  if (stations.length === 1) return stations[0].toString();
  return `${stations[0]}-${stations[stations.length - 1]}`;
}

/**
 * Get the valid stations (filters out stations > 10)
 * @param {number} stationConfig - Station configuration
 * @returns {number[]} Array of valid station numbers (≤ 10)
 */
export function getValidStationsForConfig(stationConfig) {
  return getStationsForConfig(stationConfig).filter(station => station <= 10);
}

/**
 * Generate per-station field names for database columns
 * @param {number} stationNumber - Station number (1-10)
 * @param {string} fieldName - Field name (e.g., 'active_button', 'red_light')
 * @returns {string} Database column name (e.g., 'station_1_active_button')
 */
export function getStationFieldName(stationNumber, fieldName) {
  return `station_${stationNumber}_${fieldName}`;
}

/**
 * Generate MQTT command name for per-station commands
 * @param {number} stationNumber - Station number (1-10)
 * @param {string} commandName - Command name (e.g., 'ActiveButton', 'RedLight')
 * @returns {string} MQTT command name (e.g., 'MachineState/Station1ActiveButton')
 */
export function getStationMqttCommand(stationNumber, commandName) {
  return `MachineState/Station${stationNumber}${commandName}`;
}

/**
 * Get all per-station settings fields for a given station configuration
 * @param {number} stationConfig - Station configuration
 * @returns {Object} Object with station numbers as keys and field arrays as values
 */
export function getPerStationFields(stationConfig) {
  const validStations = getValidStationsForConfig(stationConfig);
  const fields = [
    'active_button',
    'red_light', 
    'yellow_light',
    'green_light',
    'pocket_door_1',
    'pocket_door_2', 
    'pocket_door_3',
    'pocket_door_4',
    'weigh_hopper_door',
    'call_for_infeed_button',
    'call_for_infeed_blue_light',
    // Per-station weight scale settings
    'scale_min_weight',
    'scale_max_weight',
    'scale_variance',
    'scale_update_interval'
  ];
  
  const result = {};
  validStations.forEach(stationNum => {
    result[stationNum] = fields.map(field => getStationFieldName(stationNum, field));
  });
  
  return result;
}